import { distance, moveTowards, vec2 } from './geometry.js';
import { modifierTotal } from './items.js';
import { TICK_RATE_HZ } from './tick.js';
/**
 * Walking an entity along a path, one fixed step at a time.
 *
 * This is where the three spatial layers meet. `grid.ts` says where an entity
 * may stand, `pathfinding.ts` says which way it goes, `geometry.ts` does the
 * arithmetic — and this module spends a step's worth of distance on the route
 * they produced, waypoint by waypoint, until the route runs out.
 *
 * Everything is in **cell coordinates**, like the rest of the core. Speeds are
 * cells per second and distances are cells; how big a cell is on screen is the
 * renderer's business and no pixel constant belongs in here (`TODO.md` #42).
 *
 * A waypoint names a **cell**, and an entity sent to a cell stands in the
 * *middle* of it. `createWalker` is the one place that turns the pathfinder's
 * whole numbers into the points an entity walks (`cellCentre`), so nothing
 * downstream — least of all the renderer — ever adds an offset of its own.
 *
 * ## Why it is written this way
 *
 * The server runs this as truth and the client runs the identical code to
 * predict its own movement, so the two must agree bit for bit or the client
 * spends the run being snapped back. Three rules buy that:
 *
 *   - **The step is always exactly one tick**, `TICK_MS` of simulation, never a
 *     frame's `dt`. `tick.ts` owns the rate; nothing here re-derives it.
 *   - **No hidden state.** A `Walker` in, a `Walker` out. No mutation, no
 *     clock, no randomness — a walk is a pure function of where it started.
 *   - **Distance is conserved across waypoints.** A step that reaches a corner
 *     carries its unspent remainder into the next leg. Dropping it would tax
 *     every turn, so a string-pulled diagonal route — which is nothing but
 *     corners — would crawl next to a straight one of the same length.
 *
 * Arrival is a snap, not a comparison: a step that reaches or overshoots a
 * waypoint lands the entity *on* it (`geometry.moveTowards` returns the target
 * itself), so "am I there yet" never needs an epsilon and both sides answer it
 * identically.
 *
 * The route is walked as given. Nothing here re-reads the grid, so a map that
 * changes under a walker — a door, a collapsing floor — is the caller's problem
 * to notice and re-path (`TODO.md` #53).
 */
/**
 * The middle of the cell a point falls in — the one definition of the half-cell
 * offset (`TODO.md` #50).
 *
 * `findPath` numbers cells, and the number `3` names the whole of cell 3, not
 * the seam at its left edge. Walked raw, those integers put an entity on the
 * corner where four cells meet — up to three of which may be rock — so a route
 * hugs cell boundaries instead of running down their middles. A waypoint
 * therefore *means* the centre of its cell, and this function is the only place
 * that says so: a second copy of the `+0.5` would offset prediction and truth
 * differently, which is the one bug this module exists to prevent.
 *
 * It is also what makes a walked route agree with the field it was eroded by
 * (`TODO.md` #70). `clearance.ts` measures the largest disc that fits *centred*
 * on a cell; after this, the line an entity walks is the line that was measured,
 * rather than one half a cell off it on each axis.
 *
 * Resolves by `Math.floor`, like every other position query in core, which
 * makes it **idempotent**: the centre of a centre is that same centre, so a
 * route that passes through twice does not drift another half cell.
 */
export const cellCentre = (cell) => vec2(Math.floor(cell.x) + 0.5, Math.floor(cell.y) + 0.5);
/**
 * How fast a **character** walks before anything modifies it, in cells per
 * second. A placeholder base, not a constant everything shares.
 *
 * Nothing in the game yields a *base* speed yet: `stats.ts` has no movement
 * attribute, there is no class base and no run-vs-walk distinction
 * (`TODO.md` #51). Rather than let every call site invent its own number —
 * which is how client and server end up walking different distances — core
 * states one, here, beside the code that consumes it.
 *
 * Four cells a second: a step of `4/20` = a fifth of a cell, and a cell crossed
 * every quarter second. A body is about a cell wide (`TODO.md` #71), so that
 * reads as a brisk walk rather than a shuffle or a blur. It is tuned by feel
 * once something is on screen, and it is replaced outright — not merely
 * re-tuned — when the per-character stat tables land in M5.
 *
 * What it is **not**, any more, is what a monster walks at. `ARCHETYPE_SPEED`
 * (`monster.ts`) states the monster bases, every one of them under this, which
 * is what makes retreating from a pack a move a player can actually play.
 */
export const DEFAULT_MOVE_SPEED = 4;
const assertSpeed = (speed) => {
    // Phrased as a negated comparison so NaN is rejected along with the negatives:
    // one NaN would spread through every position the entity touched afterwards.
    if (!(Number.isFinite(speed) && speed >= 0)) {
        throw new RangeError(`A walker's speed must be a finite, non-negative number of cells per second, received ${speed}`);
    }
};
/**
 * The ground a walker of this speed covers in one fixed step.
 *
 * The single definition of the cells-per-second → cells-per-step conversion.
 * Written as a division by the rate rather than a multiplication by a
 * pre-computed step length, because the two disagree in the last bits and only
 * one of them may exist.
 */
export const stepDistance = (speed) => {
    assertSpeed(speed);
    return speed / TICK_RATE_HZ;
};
/** What a whole of something is, as a percentage. Named so the arithmetic below reads as the rule it is. */
const PERCENT = 100;
/**
 * A base speed with everything that moves it applied — the one place a speed
 * becomes *this body's* speed.
 *
 * ## Why it is a stat and not a table lookup
 *
 * Speed is the last number in this game that was a constant. Gear moves it (D2
 * puts Faster Run/Walk on boots and jewellery), a pack's rank and its Extra Fast
 * affix move it (`elites.ts` declares +100% and had nothing to spend it on), and
 * a chill or a haste will move it the moment either exists — and every one of
 * those already speaks one language: an `ItemModifier` carrying an `ItemStat`.
 * So speed joins that language rather than growing a second one beside it, and
 * `effects.ts`'s `activeModifiers` feeds this function without any adapter at
 * all: a timed buff on a body *is* a list of modifiers.
 *
 * ## The arithmetic, and why it is addition
 *
 * `movementSpeedPercent` is **added across every source and applied once** —
 * D2's own rule for Faster Run/Walk, and the same one `elites.ts` states for
 * merging two scalings ("the rest add"). Two +50% sources are +100%, never
 * ×1.5×1.5; the two readings agree at one source and diverge at two, which is
 * exactly the case a champion wearing an affix produces.
 *
 * A total below −100% is **rooted, not reversed**: stacked slows are an ordinary
 * outcome and a negative speed would walk a body backwards down its own route.
 * Zero is a legal speed everywhere here — `stepDistance(0)` is zero ground — so
 * a rooted body simply stands.
 *
 * The result is a **speed**, in cells per second, and it reaches the world
 * through `stepDistance` like every other one. Nothing here pre-computes a
 * per-tick step: that division exists once, below, because the two forms
 * disagree in the last bits and only one of them may exist.
 */
export const moveSpeed = (base, modifiers = []) => {
    assertSpeed(base);
    return Math.max(0, (base * (PERCENT + modifierTotal(modifiers, 'movementSpeedPercent'))) / PERCENT);
};
/**
 * A walker at `position` bound for `path`, at `speed` cells per second.
 *
 * This is where a route of cells becomes a route of points: every waypoint is
 * taken to `cellCentre`, so an entity ordered onto a cell ends up standing in
 * the middle of it and a corridor is walked down its lane rather than along its
 * wall.
 *
 * The leading waypoint is dropped when the entity is already standing in that
 * cell. `findPath` opens its route with the *start* cell, and under the centre
 * convention that waypoint is the middle of the cell the entity is in already:
 * everything it could contribute is true before the first step, and walking it
 * would pull anything not sitting on the exact middle backwards, off its
 * heading, before it set off — a lurch at the start of every click-to-move, in
 * the opposite direction to the click at that. It follows that ordering an
 * entity to the cell it already occupies leaves nothing to walk: no step, and no
 * shuffle onto the middle either.
 *
 * "Standing in that cell" is a comparison of *cells*, resolved by `Math.floor`
 * on both sides. So the rule reads the same whether it is handed a raw route or
 * a walker's own already-centred one, and rebuilding a walker mid-route is safe.
 *
 * The route is copied rather than aliased, for the reason `createGrid` copies
 * its cells: a path that can change under a walker is a desync waiting to
 * happen.
 */
export const createWalker = (position, path, speed) => {
    assertSpeed(speed);
    const head = path[0];
    const standingOnHead = head !== undefined &&
        Math.floor(position.x) === Math.floor(head.x) &&
        Math.floor(position.y) === Math.floor(head.y);
    return { position, path: path.slice(standingOnHead ? 1 : 0).map(cellCentre), speed };
};
/**
 * Advances a walker by exactly one simulation step.
 *
 * The step's distance is spent along the route rather than towards a single
 * point: each waypoint it reaches is consumed and the remainder continues
 * towards the next, so no distance is lost at a corner and no waypoint is
 * skipped over. What is left of the route comes back in the returned walker;
 * the caller keeps nothing else between steps.
 */
export const stepWalker = (walker) => {
    if (walker.path.length === 0)
        return { walker, arrived: false };
    let position = walker.position;
    let budget = stepDistance(walker.speed);
    let reached = 0;
    for (const waypoint of walker.path) {
        const gap = distance(position, waypoint);
        if (gap > budget) {
            position = moveTowards(position, waypoint, budget);
            break;
        }
        // Snapped onto the waypoint, not merely near it — arrival stays an exact
        // equality on both sides of the wire.
        position = waypoint;
        budget -= gap;
        reached += 1;
    }
    return {
        walker: { position, path: walker.path.slice(reached), speed: walker.speed },
        arrived: reached === walker.path.length,
    };
};
//# sourceMappingURL=movement.js.map